import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MonitorUp, Image as ImageIcon, Film, Cast, Square, ChevronLeft, ChevronRight, Loader2, Link2, Airplay } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MirrorSender } from "@/lib/mirrorRtc";

const Src = ({ icon, title, sub, onClick, disabled, testId, active }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId}
    className={`text-left rounded-3xl p-4 border transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0 ${active ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 bg-white/[0.04]"}`}>
    {icon}<div className="font-bold mt-2">{title}</div><div className="text-xs text-white/50">{sub}</div>
  </button>
);

export default function CastPanel({ caps }) {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [sender, setSender] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [source, setSource] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [link, setLink] = useState("");
  const preview = useRef(null);
  const canvas = useRef(null);
  const fileVid = useRef(null);
  const photoIn = useRef(null);
  const videoIn = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => { sender?.stop(); streamRef.current?.getTracks().forEach((t) => t.stop()); }, [sender]);

  const pair = async (c) => {
    setConnecting(true);
    const s = new MirrorSender(c, (t, a) => {
      if (t === "tv_left") { toast("The TV disconnected"); setSender(null); setSource(null); }
      if (t === "replaced") { toast("Another device took over this TV"); setSender(null); }
      if (t === "state") setLink(a);
    });
    try { await s.connect(); setSender(s); toast.success("Connected to TV"); } catch (e) { toast.error(e.message); }
    setConnecting(false);
  };

  const openOnCast = async () => {
    const c = String(Math.floor(100000 + Math.random() * 900000));
    try {
      const req = new window.PresentationRequest([`${window.location.origin}/mirror/tv?code=${c}`]);
      await req.start();
      setConnecting(true);
      for (let i = 0; i < 20; i++) {
        const { data } = await api.get(`/mirror/rooms/${c}`);
        if (data.exists) return pair(c);
        await new Promise((r) => setTimeout(r, 1000));
      }
      setConnecting(false); toast.error("The TV didn't open the Jixxeo TV page. Enter its code instead.");
    } catch (e) { setConnecting(false); if (e?.name !== "AbortError" && e?.name !== "NotAllowedError") toast.error("No Cast device available from this browser"); }
  };

  const go = async (stream, kind, label) => {
    streamRef.current?.getTracks().forEach((t) => { if (!stream.getTracks().includes(t)) t.stop(); });
    streamRef.current = stream; setSource(kind);
    if (preview.current && kind === "screen") preview.current.srcObject = stream;
    await sender.setStream(stream, { kind, label });
  };

  const shareScreen = async () => {
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: true });
      s.getVideoTracks()[0].onended = () => stopCast();
      await go(s, "screen", "screen share");
    } catch (e) { if (e?.name !== "NotAllowedError") toast.error("Screen sharing isn't available in this browser"); }
  };

  const drawPhoto = (img) => {
    const c = canvas.current, x = c.getContext("2d");
    x.fillStyle = "#000"; x.fillRect(0, 0, c.width, c.height);
    const k = Math.min(c.width / img.width, c.height / img.height);
    x.drawImage(img, (c.width - img.width * k) / 2, (c.height - img.height * k) / 2, img.width * k, img.height * k);
  };
  useEffect(() => {
    if (source !== "photo" || !photos[idx]) return;
    const img = new window.Image(); img.onload = () => drawPhoto(img); img.src = photos[idx];
    const t = setInterval(() => img.complete && drawPhoto(img), 700);
    return () => clearInterval(t);
  }, [source, photos, idx]);

  const pickPhotos = async (files) => {
    if (!files.length) return;
    setPhotos([...files].map((f) => URL.createObjectURL(f))); setIdx(0);
    await go(canvas.current.captureStream(10), "photo", "photos");
  };
  const pickVideo = async (f) => {
    if (!f) return;
    setSource("video");
    const v = fileVid.current; v.src = URL.createObjectURL(f);
    await v.play().catch(() => {});
    const cap = v.captureStream || v.mozCaptureStream;
    if (!cap) return toast.error("This browser can't stream video files to the Jixxeo TV page — use the device Cast/AirPlay button below");
    await go(cap.call(v), "video", f.name);
  };
  const nativeCast = async () => {
    const v = fileVid.current;
    try {
      if (v.webkitShowPlaybackTargetPicker) return v.webkitShowPlaybackTargetPicker();
      await v.remote.prompt();
    } catch { toast.error("Your browser couldn't send this file to a Cast device. Use the Jixxeo TV page or your phone's Screen Mirroring instead."); }
  };
  const stopCast = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
    sender?.send({ t: "stop" }); setSource(null);
    if (fileVid.current) { fileVid.current.pause(); }
  };
  const disconnect = () => { stopCast(); sender?.stop(); setSender(null); };

  if (!user) return (
    <div className="py-14" data-testid="mirror-login-gate">
      <h2 className="font-heading font-black text-3xl">Sign in to cast</h2>
      <p className="text-white/60 text-sm mt-2 max-w-sm">Casting uses your Jixxeo account so only you control your TV session. The setup guides and compatibility check work without signing in.</p>
      <Link to="/login?next=/mirror" className="inline-block mt-5 bg-[#00F0FF] text-black font-black rounded-full px-6 py-3" data-testid="mirror-login-btn">Sign in</Link>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="mirror-cast-panel">
      <canvas ref={canvas} width={1920} height={1080} className="hidden" />
      {!sender ? (
        <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF]">Step 1 · Connect a TV</div>
          <p className="text-sm text-white/60 mt-2">On the TV's web browser open <b className="text-white">{window.location.host}/mirror/tv</b> and type the code shown.</p>
          <div className="flex gap-2 mt-4">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" data-testid="mirror-code-input"
              className="flex-1 min-w-0 bg-black/40 border border-white/15 rounded-full px-5 py-3 mono text-xl tracking-[0.3em] outline-none focus:border-[#00F0FF]" />
            <button onClick={() => pair(code)} disabled={code.length !== 6 || connecting} data-testid="mirror-connect-btn" className="bg-[#00F0FF] text-black font-black rounded-full px-6 disabled:opacity-40">{connecting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Connect"}</button>
          </div>
          {caps.presentation && <button onClick={openOnCast} disabled={connecting} data-testid="mirror-open-chromecast" className="mt-3 w-full rounded-full border border-white/15 py-3 font-bold flex items-center justify-center gap-2 hover:bg-white/5"><Cast className="w-4 h-4" /> Open on Chromecast instead</button>}
          <a href="/mirror/tv" target="_blank" rel="noreferrer" className="mt-3 text-xs text-white/40 underline flex items-center gap-1" data-testid="mirror-tv-link"><Link2 className="w-3 h-3" />Open the TV page here to test</a>
        </section>
      ) : (
        <section className="rounded-3xl border border-[#39FF14]/30 bg-[#39FF14]/5 p-4 flex items-center gap-3" data-testid="mirror-connected">
          <span className="w-2.5 h-2.5 rounded-full bg-[#39FF14] animate-pulse" />
          <div className="flex-1 text-sm"><b>TV connected</b><span className="text-white/50"> · {source ? (link === "connected" ? "streaming" : "starting stream…") : "pick what to cast"}</span></div>
          <button onClick={disconnect} className="text-xs text-white/60 hover:text-white" data-testid="mirror-disconnect">Disconnect</button>
        </section>
      )}
      <section>
        <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-3">Step 2 · Choose what to cast</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Src icon={<MonitorUp className="w-6 h-6 text-[#00F0FF]" />} title="Screen or tab" sub={caps.screen ? "Mirror your display or one tab" : "Not supported on this device"} onClick={shareScreen} disabled={!sender || !caps.screen} testId="mirror-src-screen" active={source === "screen"} />
          <Src icon={<ImageIcon className="w-6 h-6 text-[#FF2BD6]" />} title="Photos" sub="Slideshow from your device" onClick={() => photoIn.current.click()} disabled={!sender} testId="mirror-src-photos" active={source === "photo"} />
          <Src icon={<Film className="w-6 h-6 text-[#FFE300]" />} title="Video" sub="Play a video file on the TV" onClick={() => videoIn.current.click()} testId="mirror-src-video" active={source === "video"} />
        </div>
        <input ref={photoIn} type="file" accept="image/*" multiple hidden onChange={(e) => { pickPhotos(e.target.files); e.target.value = ""; }} data-testid="mirror-photo-input" />
        <input ref={videoIn} type="file" accept="video/*" hidden onChange={(e) => { if (sender) pickVideo(e.target.files[0]); else { setSource("video"); fileVid.current.src = URL.createObjectURL(e.target.files[0]); } e.target.value = ""; }} data-testid="mirror-video-input" />
        {!sender && <p className="text-[11px] text-white/40 mt-2">Connect a TV first — or pick a video to use your device's own Cast / AirPlay button.</p>}
      </section>
      <section className={source ? "" : "hidden"} data-testid="mirror-preview">
        <div className="mono text-[10px] uppercase tracking-widest text-white/40 mb-2">Preview</div>
        <video ref={preview} autoPlay muted playsInline className={`w-full rounded-2xl bg-black aspect-video ${source === "screen" ? "" : "hidden"}`} />
        {source === "photo" && photos[idx] && (
          <div className="relative">
            <img src={photos[idx]} alt="" className="w-full rounded-2xl bg-black aspect-video object-contain" data-testid="mirror-photo-preview" />
            {photos.length > 1 && <>
              <button onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)} data-testid="mirror-photo-prev" className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center"><ChevronLeft /></button>
              <button onClick={() => setIdx((i) => (i + 1) % photos.length)} data-testid="mirror-photo-next" className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center"><ChevronRight /></button>
              <span className="absolute bottom-2 right-3 mono text-xs bg-black/60 px-2 rounded">{idx + 1}/{photos.length}</span>
            </>}
          </div>
        )}
        <video ref={fileVid} controls playsInline className={`w-full rounded-2xl bg-black aspect-video ${source === "video" ? "" : "hidden"}`} data-testid="mirror-video-preview" />
        <div className="flex flex-wrap gap-2 mt-3">
          {source === "video" && (caps.remote || caps.airplay) && <button onClick={nativeCast} data-testid="mirror-native-cast" className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold flex items-center gap-2">{caps.airplay ? <Airplay className="w-4 h-4" /> : <Cast className="w-4 h-4" />}{caps.airplay ? "AirPlay this video" : "Cast with device"}</button>}
          {sender && source && <button onClick={stopCast} data-testid="mirror-stop-cast" className="rounded-full bg-[#FF003C] px-4 py-2 text-sm font-bold flex items-center gap-2"><Square className="w-4 h-4" /> Stop casting</button>}
        </div>
      </section>
    </div>
  );
}
